import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const TOKEN_PACKAGES = [
  { id: 'basic', tokens: 5, price: 1.50, name: 'Basic Package' },
  { id: 'standard', tokens: 15, price: 3.00, name: 'Standard Package' },
  { id: 'premium', tokens: 35, price: 5.00, name: 'Premium Package' },
];

const BuyTokens: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async (packageId: string) => {
    try {
      setLoading(packageId);
      setError(null);
      
      const session = await userApi.createTokenPurchaseSession(packageId);

      // Load Stripe
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe failed to load');

      // Redirect to Stripe Checkout with the first session
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: session.data.sessionId
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during checkout');
      setLoading(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Purchase Tokens
      </Typography>
      
      <Typography variant="body1" align="center" sx={{ mb: 4 }}>
        Each postcard generation costs 1 token. Choose a package that suits your needs.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {TOKEN_PACKAGES.map((pkg) => (
          <Grid item xs={12} sm={6} md={4} key={pkg.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {pkg.name}
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  £{pkg.price.toFixed(2)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {pkg.tokens} tokens
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {pkg.tokens} postcards
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={loading === pkg.id}
                >
                  {loading === pkg.id ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Purchase'
                  )}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default BuyTokens; 